import {
  Box,
  Button,
  Card,
  Flex,
  Image,
  Link,
  Slide,
  Stack,
  Text,
  chakra,
  keyframes,
  useDisclosure,
  Collapse,
  Tag,
  TagLabel,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import { PLAN_PROPERTIES_MAP, getPlanFeatures } from '#constants';
import PricingFeature from './PricingFeature.jsx';
import { Link as ReactLink } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import { useUIStore } from '#store';

export default function PricingPlan({
  frequency,
  offer,
  onClick,
  monthly,
  annual,
  outingPriority,
}) {
  const { getPlanCost } = useUIStore(
    (state) => ({
      getPlanCost: state.getPlanCost,
    }),
    shallow
  );
  const slideUp = keyframes`
    20%, 100% {
      transform: translate3d(0,-100%,-10px);
    }
  `;

  const plan = frequency === 'MONTHLY' ? monthly : annual;
  const { name, cadence, slug } = plan;
  const planProperties = PLAN_PROPERTIES_MAP[slug];
  const { priority, buttonText } = planProperties;
  const features = getPlanFeatures({ slug, outingPriority });
  const freqText =
    cadence === 'MONTHLY' ? 'month' : cadence === 'ANNUAL' ? 'year' : '';
  // Get cost using store function
  const cost = getPlanCost(slug);

  return (
    <Box
      rounded={['none', 'lg']}
      shadow={['none', 'md']}
      bg="white"
      position="relative"
      sx={{ transformStyle: 'preserve-3d' }}
    >
      {offer && (
        <Link
          as={ReactLink}
          position={'absolute'}
          w="100%"
          transform="translateZ(-10px)"
          animation={`${slideUp} 5s linear 0s infinite forwards`}
          to={'/offers/money-back'}
          isExternal
        >
          <Text
            bg={'secondary.600'}
            py={[4]}
            borderTopRadius="lg"
            color="white"
            fontSize={['md', 'lg']}
            textAlign={'center'}
            _hover={{
              textDecoration: 'underline',
            }}
          >
            No availability, 100% credit refund.
          </Text>
        </Link>
      )}
      {cadence === 'MONTHLY' && slug !== 'pay-as-you-go' && (
        <Stack
          position={'absolute'}
          top={['-50px', '-35px']}
          transform="translateZ(-10px)"
          w="100%"
        >
          <Text
            bg={'secondary.600'}
            py={[1.5, 1.5]}
            borderTopRadius="lg"
            color="white"
            fontSize={['sm', 'md']}
            textAlign={'center'}
          >
            Switch to yearly plan to save 55% !
          </Text>
        </Stack>
      )}
      <Flex
        direction="column"
        justify="space-between"
        p="6"
        borderBottomWidth="1px"
        color="gray.200"
        align={'center'}
      >
        <chakra.p
          mb={1}
          fontSize={['md', 'lg']}
          fontWeight="semibold"
          color="gray.700"
        >
          {name}
        </chakra.p>
        <Text
          mb={2}
          fontSize={['4xl', '5xl']}
          fontWeight={['bold', 'extrabold']}
          color="gray.900"
          _dark={{
            color: 'gray.50',
          }}
          lineHeight="tight"
        >
          ${cost}
          {freqText && (
            <chakra.span
              fontSize={['xl', '2xl']}
              fontWeight="medium"
              color="gray.600"
              _dark={{
                color: 'gray.400',
              }}
            >
              {' '}
              / {freqText}
            </chakra.span>
          )}
        </Text>
        <Button
          w={['full', , '100%']}
          onClick={() => onClick(plan.slug)}
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          px={5}
          py={5}
          border="solid transparent"
          fontWeight="bold"
          rounded="md"
          shadow="md"
          color={'white'}
          bg={priority === 'primary' ? 'brand.500' : 'secondary.500'}
          _hover={{
            bg: priority === 'primary' ? 'brand.600' : 'secondary.300',
          }}
        >
          {buttonText}
        </Button>
      </Flex>
      <Stack direction="column" p="6" spacing={[1, '10px']} flexGrow="1">
        {features.map((feat, idx) => (
          <PricingFeature feat={feat} key={idx} />
        ))}
      </Stack>
    </Box>
  );
}

export function MobilePricing({ frequency, offer, monthly, annual, outingPriority }) {
  const { planSlug, setPlanSlug, getPlanCost } = useUIStore(
    (state) => ({
      planSlug: state.planSlug,
      setPlanSlug: state.setPlanSlug,
      getPlanCost: state.getPlanCost,
    }),
    shallow
  );
  const slideUp = keyframes`
    20%, 100% {
      transform: translate3d(0,-100%,-10px);
    }
  `;
  const [show, setShow] = useState(false);

  const handleToggle = () => setShow(!show);

  const plan = frequency === 'MONTHLY' ? monthly : annual;
  const { name, cadence, slug } = plan;
  const planProperties = PLAN_PROPERTIES_MAP[slug];
  const { priority, buttonText } = planProperties;
  const features = getPlanFeatures({ slug, outingPriority });
  const freqText =
    cadence === 'MONTHLY' ? 'month' : cadence === 'ANNUAL' ? 'year' : '';
  // Get cost using store function
  const cost = getPlanCost(slug);
  const currentPlanSelected = planSlug === slug;
  return (
    <Box
      rounded={['none', 'lg']}
      shadow={['none', 'md']}
      bg="white"
      position="relative"
      sx={{ transformStyle: 'preserve-3d' }}
      borderRadius={'lg'}
      borderWidth={'2px'}
      borderColor={currentPlanSelected ? 'brand.700' : 'brand.100'}
      borderTopColor={currentPlanSelected ? 'none' : 'brand.100'}
      onClick={() => setPlanSlug(slug)}
      mt={priority === 'primary' ? 4 : 0}
    >
      {priority === 'primary' && (
        <Text
          bg={'secondary.600'}
          py={[1]}
          borderTopRadius="lg"
          color="white"
          fontSize={['md', 'lg']}
          textAlign={'center'}
          position={'absolute'}
          fontWeight={500}
          w="100%"
          transform="translateZ(-10px)"
          animation={`${slideUp} 3s linear 0s forwards`}
        >
          32% more success rate!
        </Text>
      )}
      <Flex
        justify="space-between"
        pt={2}
        px={4}
        borderBottomWidth="1px"
        color="gray.200"
        align={'center'}
      >
        <Text
          mb={1}
          fontSize={['md', 'lg']}
          fontWeight="semibold"
          color="gray.700"
          maxW={['50%']}
        >
          {name}
        </Text>
        <Text
          mb={2}
          fontSize={['4xl', '5xl']}
          fontWeight={['bold', 'extrabold']}
          color="gray.900"
          _dark={{
            color: 'gray.50',
          }}
          lineHeight="tight"
        >
          ${cost}
          {freqText && (
            <chakra.span
              fontSize={['md', '2xl']}
              fontWeight="medium"
              color="gray.600"
              _dark={{
                color: 'gray.400',
              }}
            >
              {' '}
              / {freqText}
            </chakra.span>
          )}
        </Text>
      </Flex>
      <Collapse startingHeight={'65px'} in={show}>
        <Stack
          direction="column"
          pt={2}
          spacing={[1, '10px']}
          flexGrow="1"
          w="100%"
          px={2}
        >
          {features.map((feat, idx) => (
            <PricingFeature feat={feat} key={idx} />
          ))}
        </Stack>
      </Collapse>
      <Flex justifyContent={'space-between'} ml={4} mb={2} mr={2}>
        <Text
          fontSize={'sm'}
          onClick={handleToggle}
          variant={'ghost'}
          color={'brand.500'}
          cursor={'pointer'}
          bg="none"
        >
          Show {show ? 'Less' : 'More'}
        </Text>
        {/* {slug === "adventurer-monthly-pass" && <Tag colorScheme='green' size={'sm'} borderRadius='full' variant='outline'>
          <TagLabel fontSize={'0.8rem'}>20% more success rate!</TagLabel>
        </Tag>} */}
      </Flex>
    </Box>
  );
}
